import React, { useCallback, useEffect, useState } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from '../services/api';
import {
  Activity,
  AlertTriangle,
  ArrowUpRight,
  Building2,
  Car,
  CheckCircle2,
  CreditCard,
  FileText,
  Inbox,
  Map,
  RefreshCw,
  ScanFace,
  ShieldCheck,
  Sliders,
  TrendingUp,
  Truck,
  Users,
  Wallet,
} from 'lucide-react';
import {
  SkeletonDashboard,
  statusColor as kitStatusColors,
  statusLabel as kitStatusLabels,
  CHART,
  chartAxis,
  chartTooltip,
} from '../ui/kit';
import { useReviewInbox } from '../components/ReviewInboxContext';
import { queueLinkForBereich } from '../lib/reviewLogic.mjs';
import './Dashboard.css';
import PageHeader from '../components/PageHeader';
import { datumUndZeit, uhrzeit } from '../lib/zeit';
import { RideDetailModal, rideDetailFromResponse } from './Rides';

// RESERVIERUNG-KOMPLETT-001: kein Rohwort „no_driver“ im Dashboard (kit.js bleibt die synchrone Design-System-Kopie).
const statusLabels = { ...kitStatusLabels, no_driver: 'Kein Fahrer gefunden' };
const statusColors = { ...kitStatusColors, no_driver: '#a16207' };

const number = new Intl.NumberFormat('de-DE', { maximumFractionDigits: 0 });
const currency = new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' });

const safeNumber = (value) => (Number.isFinite(Number(value)) ? Number(value) : 0);
const formatNumber = (value) => number.format(safeNumber(value));
const formatCurrency = (value) => currency.format(safeNumber(value));

const serviceTone = (status) => (
  status === 'ok' ? 'ok' : status === 'down' ? 'down' : status === 'warning' ? 'warning' : 'unknown'
);

function DashboardMetric({ icon: Icon, label, value, detail, onClick, attention = false }) {
  return (
    <button type="button" className={`admin-dash-metric ${attention ? 'is-attention' : ''}`} onClick={onClick}>
      <span className="admin-dash-metric-head">
        <span>{label}</span>
        <span className="admin-dash-metric-icon"><Icon size={17} strokeWidth={1.8} /></span>
      </span>
      <strong>{value}</strong>
      <small>{detail}</small>
      <ArrowUpRight className="admin-dash-corner" size={14} />
    </button>
  );
}

function QuickTile({ icon: Icon, label, onClick, detail }) {
  return (
    <button type="button" className="admin-quick-tile" onClick={onClick}>
      <span className="admin-quick-icon"><Icon size={18} strokeWidth={1.8} /></span>
      <span className="admin-quick-copy">
        <div className="admin-quick-label">{label}</div>
        <small>{detail}</small>
      </span>
      <ArrowUpRight size={14} />
    </button>
  );
}

function HealthOverview({ health, unavailable }) {
  const services = health?.services || [];
  const alerts = health?.alerts || [];
  const downCount = services.filter((service) => service.status === 'down').length;

  return (
    <section className="admin-health-panel" aria-label="VOOGO Systemstatus">
      <div className="admin-health-summary">
        <span className={`admin-health-symbol ${downCount > 0 || unavailable ? 'is-down' : ''}`}>
          {downCount > 0 || unavailable ? <AlertTriangle size={18} /> : <CheckCircle2 size={18} />}
        </span>
        <div>
          <span>Systemstatus</span>
          <strong>{unavailable ? 'Status nicht erreichbar' : downCount > 0 ? `${downCount} Dienst${downCount === 1 ? '' : 'e'} gestört` : 'Betriebssysteme erreichbar'}</strong>
        </div>
      </div>
      <div className="admin-service-list" role="list">
        {services.map((service) => {
          // SERVER-TAKT-MAPBOX-AMPEL-001 (Teil 3): Name nie kürzen; ein langer Detailtext wird höchstens
          // am Ende gekürzt und steht vollständig in title und aria-label.
          const full = service.detail ? `${service.label}: ${service.detail}` : service.label;
          return (
            <span className={`admin-service is-${serviceTone(service.status)}`} key={service.key} title={full} aria-label={full} role="listitem">
              <i aria-hidden="true" />
              <span className="admin-service-name">{service.label}</span>
              {service.detail ? <small>{service.detail}</small> : null}
            </span>
          );
        })}
        {services.length === 0 && <span className="admin-service-placeholder">Keine aktuellen Dienstdaten</span>}
      </div>
      <div className="admin-health-alerts">
        {alerts.length === 0 ? (
          <span className="admin-health-clear"><CheckCircle2 size={14} /> Keine Betriebswarnung</span>
        ) : alerts.map((alert, index) => (
          <span className={`admin-health-alert is-${alert.level || 'info'}`} key={`${alert.message}-${index}`}>
            <AlertTriangle size={13} /> <span>{alert.message}</span>
          </span>
        ))}
      </div>
    </section>
  );
}

// ADMIN-UX-HEUTE-AKTEN-EINHEIT-001: eine Zeile der „Heute"-Handlungsliste — offener Vorgang
// mit Zähler und Direktsprung in den Bereich, in dem er erledigt wird.
function TodayItem({ icon: Icon, count, label, detail, onClick }) {
  return (
    <button type="button" className="admin-today-item" onClick={onClick}>
      <span className="admin-today-icon"><Icon size={16} strokeWidth={1.8} /></span>
      <span className="admin-review-count has-open">{formatNumber(count)}</span>
      <span>
        <strong>{label}</strong>
        <small>{detail}</small>
      </span>
      <ArrowUpRight size={14} />
    </button>
  );
}

function ReviewQueue({ label, value, detail, onClick }) {
  return (
    <button type="button" className="admin-review-queue" onClick={onClick}>
      <span className={`admin-review-count ${value > 0 ? 'has-open' : ''}`}>{formatNumber(value)}</span>
      <span>
        <strong>{label}</strong>
        <small>{detail}</small>
      </span>
      <ArrowUpRight size={14} />
    </button>
  );
}

function EmptyRides() {
  return (
    <tr>
      <td className="admin-rides-empty" colSpan="7">Für diesen Status sind keine aktuellen Fahrten vorhanden.</td>
    </tr>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();
  const { badges } = useReviewInbox();
  const [data, setData] = useState(null);
  const [health, setHealth] = useState(null);
  const [today, setToday] = useState(null);   // ADMIN-UX-HEUTE: Zähler aus GET /admin/today
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [dashboardError, setDashboardError] = useState('');
  const [healthError, setHealthError] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [rideFilter, setRideFilter] = useState('');
  const [selectedRide, setSelectedRide] = useState(null);

  const load = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setRefreshing(true);
    const [dashboardResult, healthResult, todayResult] = await Promise.allSettled([
      api.get('/admin/dashboard'),
      api.get('/admin/system-health'),
      api.get('/admin/today'),
    ]);

    if (dashboardResult.status === 'fulfilled') {
      setData(dashboardResult.value.data);
      setDashboardError('');
      setLastUpdated(new Date());
    } else {
      setDashboardError('Das Admin-Dashboard konnte nicht aktualisiert werden.');
    }

    if (healthResult.status === 'fulfilled') {
      setHealth(healthResult.value.data);
      setHealthError(false);
    } else {
      setHealthError(true);
    }

    // Scheitert nur /admin/today, bleiben die zuletzt bestätigten Zähler stehen —
    // die „Heute"-Liste zeigt lieber den letzten Stand als plötzlich „alles erledigt".
    if (todayResult.status === 'fulfilled') setToday(todayResult.value.data);

    setLoading(false);
    if (!silent) setRefreshing(false);
  }, []);

  useEffect(() => {
    load();
    const interval = setInterval(() => load({ silent: true }), 8000);
    return () => clearInterval(interval);
  }, [load]);

  // ADMIN-DASHBOARD-FAHRT-DETAIL-001: Klick auf eine Zeile unter „Letzte Fahrten" öffnet
  // dasselbe Fahrt-Detail wie der Fahrten-Reiter — gleiche Ladeanfrage, gleiche Aktionen.
  const openRideDetail = async (id) => {
    try {
      const response = await api.get(`/admin/rides/${id}`);
      setSelectedRide(rideDetailFromResponse(response.data));
    } catch {
      toast.error('Fehler beim Laden');
    }
  };

  const forceCancelRide = async (id) => {
    if (!window.confirm('Fahrt wirklich stornieren?')) return;
    try {
      await api.post(`/admin/rides/${id}/cancel`);
      toast.success('Fahrt storniert');
      setSelectedRide(null);
      load({ silent: true });
    } catch (requestError) {
      toast.error(requestError.response?.data?.error || 'Fehler');
    }
  };

  if (loading && !data) return <SkeletonDashboard />;

  if (!data) {
    return (
      <section className="admin-dash-unavailable" role="alert">
        <AlertTriangle size={22} />
        <div>
          <strong>Admin-Dashboard nicht verfügbar</strong>
          <p>{dashboardError}</p>
        </div>
        <button type="button" onClick={() => load()} disabled={refreshing}>
          <RefreshCw size={15} /> Erneut versuchen
        </button>
      </section>
    );
  }

  const stats = data.stats || {};
  const recentRides = Array.isArray(data.recent_rides) ? data.recent_rides : [];
  const ridesByStatus = Array.isArray(data.rides_by_status) ? data.rides_by_status : [];
  const dailyStats = Array.isArray(data.daily_stats) ? data.daily_stats : [];
  const visibleRides = recentRides.filter((ride) => !rideFilter || ride.status === rideFilter);
  const totalReview = safeNumber(badges.fahrer) + safeNumber(badges.partner) + safeNumber(badges.fahrzeug) + safeNumber(badges.selfie);

  // ADMIN-UX-HEUTE-AKTEN-EINHEIT-001: „Heute" beantwortet beim Öffnen die Frage
  // „was braucht jetzt meine Aufmerksamkeit?" — eine Liste, alle Bereiche, nur Offenes.
  // payouts_unpaid ist ohne finance.manage null → safeNumber(null)=0 → Zeile erscheint nicht.
  const todayItems = [
    // KASSIERMOMENT-BINDET-001 (Teil B): Eine Fahrt kann dauerhaft in „unterwegs" haengen —
    // kein Timeout und kein Alarm fasst sie an. Solange sie haengt, kann der Kunde nicht neu
    // bestellen und die Provision entsteht nie. Ganz oben, weil hier moeglicherweise Bargeld
    // beim Fahrer liegt, ohne dass irgendetwas gebucht ist.
    { icon: Car, count: today?.stuck_rides, label: 'Fahrten hängen fest', detail: 'Seit über 2 Stunden unterwegs — Fahrer hat nicht abgeschlossen', route: '/rides?status=in_progress' },
    { icon: AlertTriangle, count: today?.dispatch_attention, label: 'Vermittlung prüfen', detail: '4 Rückgaben oder seit 45 Min. ohne Abholung', route: '/dispatch?attention=1' },
    { icon: Inbox, count: today?.support_active, label: 'Support-Fälle aktiv', detail: 'Tickets, Chats und Fundsachen beantworten', route: '/support' },
    { icon: CreditCard, count: today?.payouts_unpaid, label: 'Auszahlungen offen', detail: 'Berechnet oder exportiert, noch nicht bezahlt', route: '/payouts' },
    { icon: Building2, count: stats.pending_partners, label: 'Partner warten auf Freigabe', detail: 'Neuanmeldungen entscheiden', route: '/partners' },
    { icon: Truck, count: stats.pending_drivers, label: 'Fahrer in Prüfung', detail: 'Freigabe ausstehend', route: '/drivers' },
    { icon: FileText, count: stats.pending_docs, label: 'Dokumente ausstehend', detail: 'Erstupload, Reupload, Erneuerung', route: '/documents?status=pending' },
    { icon: ScanFace, count: badges.selfie, label: 'Selfie-Verifizierungen offen', detail: 'Vergleichen und entscheiden', route: '/verifications' },
  ].map((item) => ({ ...item, count: safeNumber(item.count) })).filter((item) => item.count > 0);
  const todayTotal = todayItems.reduce((sum, item) => sum + item.count, 0);

  const metrics = [
    { icon: Car, label: 'Fahrten heute', value: formatNumber(stats.today_rides), detail: `04:00–03:59 · ${formatNumber(stats.total_rides)} gesamt`, route: '/rides' },
    { icon: TrendingUp, label: 'Umsatz heute', value: formatCurrency(stats.today_revenue), detail: `04:00–03:59 · ${formatCurrency(stats.total_revenue)} gesamt`, route: '/finance' },
    { icon: Users, label: 'Kunden', value: formatNumber(stats.total_customers), detail: 'registrierte Nutzer', route: '/users' },
    { icon: Activity, label: 'Aktive Fahrten', value: formatNumber(stats.active_rides), detail: `${formatNumber(stats.online_drivers)} Fahrer online`, route: '/livemap' },
    { icon: Building2, label: 'Partner ausstehend', value: formatNumber(stats.pending_partners), detail: `${formatNumber(stats.total_partners)} Partner gesamt`, route: '/partners', attention: safeNumber(stats.pending_partners) > 0 },
    { icon: FileText, label: 'Dokumente ausstehend', value: formatNumber(stats.pending_docs), detail: `${formatNumber(stats.pending_drivers)} Fahrer in Prüfung`, route: '/documents?status=pending', attention: safeNumber(stats.pending_docs) > 0 },
  ];

  return (
    <div className="admin-dashboard">
      {/* Die Hülle „rides-page" liefert die --rides-*-Farben, die Rides.css nur dort definiert;
          das Fenster selbst liegt fest positioniert darüber, die Hülle bleibt ohne Höhe. */}
      {selectedRide && (
        <div className="rides-page">
          <RideDetailModal
            ride={selectedRide}
            onClose={() => setSelectedRide(null)}
            onCancel={forceCancelRide}
            onCompleted={() => { toast.success('Fahrt abgeschlossen.'); setSelectedRide(null); load({ silent: true }); }}
            onReassigned={() => { openRideDetail(selectedRide.id); load({ silent: true }); }}
          />
        </div>
      )}

      <PageHeader
        title="Dashboard"
        description="Systemweite Übersicht über Betrieb, Wachstum, Prüfungen und Plattformzustand."
        actions={
        <div className="admin-dash-heading-actions">
          <span className="admin-dash-live"><i /> Automatisch · 8 Sek.</span>
          <button type="button" className="admin-dash-refresh" onClick={() => load()} disabled={refreshing}>
            <RefreshCw size={15} className={refreshing ? 'is-spinning' : ''} /> Aktualisieren
          </button>
        </div>
      }
      />

      {dashboardError && (
        <div className="admin-dash-stale" role="status">
          <AlertTriangle size={15} />
          {dashboardError} Die zuletzt bestätigten Werte bleiben sichtbar.
        </div>
      )}

      <section className="admin-dash-panel admin-today" aria-label="Heute zu erledigen">
        <div className="admin-dash-panel-heading">
          <div><span>Heute</span><h2>Was jetzt Aufmerksamkeit braucht</h2></div>
          <span className={`admin-dash-total ${todayTotal > 0 ? 'has-open' : ''}`}>{formatNumber(todayTotal)} offen</span>
        </div>
        {todayItems.length === 0 ? (
          <div className="admin-today-clear"><CheckCircle2 size={16} /> Nichts offen — alles erledigt.</div>
        ) : (
          <div className="admin-today-list">
            {todayItems.map((item) => (
              <TodayItem key={item.label} {...item} onClick={() => navigate(item.route)} />
            ))}
          </div>
        )}
      </section>

      <HealthOverview health={health} unavailable={healthError} />

      <section className="admin-dash-metrics" aria-label="Systemweite VOOGO Kennzahlen">
        {metrics.map((metric) => (
          <DashboardMetric key={metric.label} {...metric} onClick={() => navigate(metric.route)} />
        ))}
      </section>

      <section className="admin-dash-work-grid">
        <article className="admin-dash-panel">
          <div className="admin-dash-panel-heading">
            <div><span>Review-Inbox</span><h2>Offene Prüfungen</h2></div>
            <span className={`admin-dash-total ${totalReview > 0 ? 'has-open' : ''}`}>{formatNumber(totalReview)} offen</span>
          </div>
          <div className="admin-review-grid">
            <ReviewQueue label="Fahrer-Dokumente" value={badges.fahrer} detail="Erstupload, Reupload, Erneuerung" onClick={() => navigate(queueLinkForBereich('fahrer'))} />
            <ReviewQueue label="Partner-Dokumente" value={badges.partner} detail="Partnerbezogene Prüfungen" onClick={() => navigate(queueLinkForBereich('partner'))} />
            <ReviewQueue label="Fahrzeug-Dokumente" value={badges.fahrzeug} detail="Unter anderem Konzessionen" onClick={() => navigate(queueLinkForBereich('fahrzeug'))} />
            <ReviewQueue label="Selfie-Verifizierungen" value={badges.selfie} detail="Vergleichs- und Entscheidungsmodus" onClick={() => navigate(queueLinkForBereich('selfie'))} />
          </div>
        </article>

        <article className="admin-dash-panel">
          <div className="admin-dash-panel-heading">
            <div><span>Arbeitsbereiche</span><h2>Schnellzugriff</h2></div>
          </div>
          <div className="admin-quick-grid">
            <QuickTile icon={Map} label="Live-Karte" detail="Flotte und Fahrten" onClick={() => navigate('/livemap')} />
            <QuickTile icon={Sliders} label="Ultimate Pricing V2" detail="Zustand und Regeln" onClick={() => navigate('/ultimate-v2')} />
            <QuickTile icon={ShieldCheck} label="Verifizierung" detail="Offene Entscheidungen" onClick={() => navigate('/verifications')} />
            <QuickTile icon={Wallet} label="Finanzen" detail="Umsatz und Auszahlungen" onClick={() => navigate('/finance')} />
            <QuickTile icon={Building2} label="Partner" detail="Unternehmen verwalten" onClick={() => navigate('/partners')} />
            <QuickTile icon={Users} label="Team" detail="Rollen und Rechte" onClick={() => navigate('/team')} />
          </div>
        </article>
      </section>

      <section className="admin-dash-insight-grid">
        <article className="admin-dash-panel admin-revenue-panel">
          <div className="admin-dash-panel-heading">
            <div><span>Plattformentwicklung</span><h2>Umsatz · letzte 30 Betriebstage</h2></div>
            <span className="admin-dash-asof">Stand {lastUpdated ? uhrzeit(lastUpdated) : '–'}</span>
          </div>
          <div className="admin-chart-wrap">
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={dailyStats} margin={{ top: 8, right: 8, bottom: 0, left: -8 }}>
                <CartesianGrid vertical={false} strokeDasharray="3 3" stroke={CHART.grid} />
                <XAxis dataKey="date" {...chartAxis} tickFormatter={(date) => date?.slice(5)} />
                <YAxis {...chartAxis} />
                <Tooltip contentStyle={chartTooltip} formatter={(value) => formatCurrency(value)} />
                <Line type="monotone" dataKey="revenue" stroke={CHART.ink} strokeWidth={2.5} dot={false} activeDot={{ r: 4, fill: CHART.brand }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </article>

        <article className="admin-dash-panel admin-status-panel">
          <div className="admin-dash-panel-heading">
            <div><span>Gesamtbestand</span><h2>Fahrten nach Status</h2></div>
            <span className="admin-dash-total">{formatNumber(stats.total_rides)}</span>
          </div>
          <div className="admin-status-list">
            {ridesByStatus.map(({ status, count }) => {
              const share = safeNumber(stats.total_rides) > 0 ? Math.min((safeNumber(count) / safeNumber(stats.total_rides)) * 100, 100) : 0;
              return (
                <div className="admin-status-row" key={status}>
                  <div><span>{statusLabels[status] || status}</span><strong>{formatNumber(count)}</strong></div>
                  <div className="admin-status-track">
                    <span style={{ width: `${share}%`, background: statusColors[status] || CHART.ink }} />
                  </div>
                </div>
              );
            })}
            {ridesByStatus.length === 0 && <div className="admin-status-empty">Noch keine Fahrten vorhanden.</div>}
          </div>
        </article>
      </section>

      <section className="admin-dash-panel admin-rides-panel">
        <div className="admin-dash-panel-heading admin-rides-heading">
          <div><span>Plattformweit</span><h2>Letzte Fahrten</h2></div>
          <div className="admin-ride-filters" aria-label="Fahrten nach Status filtern">
            {['', ...Object.keys(statusLabels)].map((status) => (
              <button
                type="button"
                key={status || 'all'}
                className={rideFilter === status ? 'is-active' : ''}
                onClick={() => setRideFilter(status)}
              >
                {status ? statusLabels[status] : 'Alle'}
              </button>
            ))}
          </div>
        </div>
        <div className="admin-rides-scroll">
          <table className="vg-table admin-rides-table">
            <thead>
              <tr>
                {['Kunde', 'Fahrer', 'Von', 'Nach', 'Preis', 'Status', 'Zeit'].map((heading) => <th key={heading}>{heading}</th>)}
              </tr>
            </thead>
            <tbody>
              {visibleRides.length === 0 ? <EmptyRides /> : visibleRides.map((ride) => (
                <tr
                  key={ride.id}
                  className="admin-ride-row"
                  onClick={() => openRideDetail(ride.id)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      openRideDetail(ride.id);
                    }
                  }}
                  tabIndex={0}
                >
                  <td><strong>{ride.customer_name}</strong></td>
                  <td>{ride.driver_name || '–'}</td>
                  <td className="admin-route-cell">{ride.pickup_address}</td>
                  <td className="admin-route-cell">{ride.dropoff_address}</td>
                  {/* A-1: ohne Abrechnung beendete Essen-Fahrt — nicht „Abgeschlossen“ mit Schätzbetrag. */}
                  <td><strong>{ride.price_in_review ? 'Preis in Prüfung' : ride.price_review_released ? 'Ohne Abrechnung' : (ride.price == null ? '–' : formatCurrency(ride.price))}</strong></td>
                  <td>
                    <span className="admin-ride-status" style={{ color: statusColors[ride.status] || '#4b5563', background: `${statusColors[ride.status] || '#4b5563'}18` }}>
                      {ride.price_in_review ? 'Preis in Prüfung' : ride.price_review_released ? 'Ohne Abrechnung' : (statusLabels[ride.status] || ride.status)}
                    </span>
                  </td>
                  <td>{datumUndZeit(ride.created_at, { jahr: false })}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
